/**
 * Subscription API response parsers
 * Transform API responses to app-friendly format
 */

/**
 * Parse error response
 * @param {Object} error - Raw error response
 * @returns {Object} Parsed error data
 */
export const parseSubscriptionErrorResponse = (error) => {
  if (!error) {
    return {
      success: false,
      message: 'Unknown error occurred',
      code: 'UNKNOWN_ERROR',
      details: null
    };
  }

  const { data, status } = error;

  if (data?.code) {
    return {
      success: false,
      code: data.code,
      message: data.message || 'An error occurred',
      details: data.details || null
    };
  }

  return {
    success: false,
    message: data?.message || data?.error || 'An error occurred',
    code: data?.code || `HTTP_${status}`,
    details: data?.details || null,
    status: status || 500
  };
};

/**
 * Parse plans retrieval response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed plans list
 */
export const parsePlansResponse = (response) => {
  if (!response) {
    return { success: false, message: 'Invalid response format', result: [] };
  }

  const { success, result, message } = response;
  return {
    success: success !== false,
    message: message || 'Plans retrieved successfully',
    result: result || []
  };
};

/**
 * Parse current subscription response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed active subscription
 */
export const parseCurrentSubscriptionResponse = (response) => {
  if (!response) {
    return { success: false, message: 'Invalid response format', result: null };
  }

  const { success, result, message } = response;
  return {
    success: success !== false,
    message: message || 'Current subscription retrieved successfully',
    result: result || null
  };
};

/**
 * Parse create subscription response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed checkout/create details
 */
export const parseCreateSubscriptionResponse = (response) => {
  if (!response) {
    return { success: false, message: 'Invalid response format', result: null };
  }

  const { success, result, message } = response;
  return {
    success: success !== false,
    message: message || 'Subscription created successfully',
    result: result || null
  };
};

/**
 * Parse subscription polling status response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed subscription status
 */
export const parseSubscriptionStatusResponse = (response) => {
  if (!response) {
    return { success: false, message: 'Invalid response format', result: null };
  }

  const { success, result, message } = response;
  return {
    success: success !== false,
    message: message || 'Subscription status retrieved successfully',
    result: result || null
  };
};

/**
 * Parse subscription payment history response
 * @param {Object} response - Raw API response
 * @returns {Object} Parsed payment history
 */
export const parsePaymentHistoryResponse = (response) => {
  if (!response) {
    return { success: false, message: 'Invalid response format', result: { payments: [], total: 0 } };
  }

  const { success, result, message } = response;
  return {
    success: success !== false,
    message: message || 'Payment history retrieved successfully',
    result: result || { payments: [], total: 0 }
  };
};
